import React, { useEffect, useState } from "react";
import {
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Dropdown, message } from "antd";
import GenericTable from "../../../Components/Tables/GenericTable";
import GenericModal from "../../../Components/Modals/GenericModal";
import VehicleLimitModal from "../../../Components/Modals/VehicleLimitModal";
import { useNavigate, useLocation } from "react-router-dom";
import "./style.css";
import { getVehicles, deleteVehicle } from "../../../API/FleetManager/Vehicles";
import usePageAccess from "../../../Utlis/usePageAccess";
import { getInitials } from "../../../Components/InitialsAvatar";

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const formatCurrency = (val) => {
  const num = parseFloat(val);
  return Number.isNaN(num) ? "-" : `$${num.toFixed(2)}`;
};

const mapVehicle = (item, index) => ({
  key: String(item.id ?? index),
  vehicle: [item.make, item.model].filter(Boolean).join(" ") || "-",
  vehicleAvatar: resolveAvatarUrl(item.avatar),
  vehicleId: item.vehicle_id || "-",
  numberPlate: item.license_plate || "-",
  driver: item.driver_name || "-",
  assignModule: "-",
  creditBalance: formatCurrency(item.credit_balance),
  creditUsed: formatCurrency(item.credit_used),
  status: capitalize(item.status) || "-",
});

const Vehicles = () => {
  const { canEdit } = usePageAccess("vehicles");
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleView = (record) => {
    navigate("/vehicle-detail", { state: { id: record.key } });
  };

  const handleEdit = (record) => {
    navigate("/edit-vehicle", { state: { id: record.key } });
  };

  const [dataSource, setDataSource] = useState([]);
  const [vehicleLimit, setVehicleLimit] = useState(0);
  const [limitInfo, setLimitInfo] = useState(null);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const res = await getVehicles();
        const list = res?.data?.data?.vehicles;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setDataSource(list.map(mapVehicle));
          setVehicleLimit(res?.data?.data?.vehicle_limit ?? 0);
          setLimitInfo(res?.data?.data);
          if (location.state?.showLimitModal) {
            setIsLimitModalOpen(true);
            navigate(location.pathname, { replace: true, state: null });
          }
        } else {
          message.error(getErrorMessage(res?.data, "Failed to load vehicles."));
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load vehicles."),
        );
      }
    };

    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moduleFilters = [
    ...new Set(dataSource.map((item) => item.assignModule)),
  ].map((module) => ({
    text: module,
    value: module,
  }));

  const statusFilters = [
    ...new Set(dataSource.map((item) => item.status).filter((v) => v !== "-")),
  ].map((status) => ({
    text: status,
    value: status,
  }));

  const columns = [
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {record.vehicleAvatar ? (
            <img
              src={record.vehicleAvatar}
              alt={text}
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : (
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                color: "#fff",
                background: getAvatarColor(text),
                flexShrink: 0,
              }}
            >
              {getInitials(text)}
            </span>
          )}
          <strong style={{ color: "#111827" }}>{text}</strong>
        </span>
      ),
      enableSearch: true,
    },
    {
      title: "Number Plate",
      dataIndex: "numberPlate",
      key: "numberPlate",
      enableSearch: true,
    },
    {
      title: "Vehicle ID",
      dataIndex: "vehicleId",
      key: "vehicleId",
      enableSearch: true,
    },

    {
      title: "Driver",
      dataIndex: "driver",
      key: "driver",
      enableSearch: true,
    },
    {
      title: "Credit Balance",
      dataIndex: "creditBalance",
      key: "creditBalance",
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
      sorter: (a, b) =>
        (parseFloat(a.creditBalance.replace(/[^0-9.-]+/g, "")) || 0) -
        (parseFloat(b.creditBalance.replace(/[^0-9.-]+/g, "")) || 0),
    },
    {
      title: "Credit Used",
      dataIndex: "creditUsed",
      key: "creditUsed",
      render: (text) => (
        <span style={{ color: "#4F46E5", fontWeight: 500 }}>{text}</span>
      ),
      sorter: (a, b) => {
        const parseCurrency = (val) =>
          parseFloat(val.replace(/[$,]/g, "")) || 0;

        return parseCurrency(a.creditUsed) - parseCurrency(b.creditUsed);
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={`status-badge ${
            text === "Active" ? "active" : "inactive"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              canEdit && {
                key: "edit",
                label: <span style={{ fontWeight: 600 }}>Edit</span>,
                icon: <EditOutlined />,
                onClick: () => handleEdit(record),
              },
              {
                key: "view",
                label: <span style={{ fontWeight: 600 }}>View</span>,
                icon: <EyeOutlined />,
                onClick: () => handleView(record),
              },

              canEdit && {
                key: "delete",
                label: (
                  <span style={{ fontWeight: 600, color: "#DC2626" }}>
                    Delete
                  </span>
                ),
                icon: <DeleteOutlined style={{ color: "#DC2626" }} />,
                onClick: () => {
                  setSelectedRow(record);
                  setIsDeleteOpen(true);
                },
              },
            ].filter(Boolean),
          }}
        >
          <BsThreeDotsVertical
            style={{ cursor: "pointer", color: "#4F46E5", fontSize: 18 }}
          />
        </Dropdown>
      ),
    },
  ];

  const handleDelete = async () => {
    if (!selectedRow) return;

    try {
      const res = await deleteVehicle(selectedRow.key);

      if (Math.floor(res?.status / 100) === 2) {
        setDataSource((prev) =>
          prev.filter((item) => item.key !== selectedRow.key),
        );
        message.success(
          getErrorMessage(res?.data, "Vehicle deleted successfully"),
        );
      } else {
        message.error(getErrorMessage(res?.data, "Failed to delete vehicle."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to delete vehicle."),
      );
    } finally {
      setIsDeleteOpen(false);
      setSelectedRow(null);
    }
  };

  const handleAdd = () => {
    if (limitInfo && Number(limitInfo.vehicle_slots_remaining) <= 0) {
      setIsLimitModalOpen(true);
      return;
    }
    navigate("/add-vehicle");
  };

  const billingCycle = String(limitInfo?.plan?.billing_cycle || "").toLowerCase();
  const isAnnual = ["annual", "annually", "yearly", "year"].includes(billingCycle);
  const planName = billingCycle
    ? isAnnual
      ? "Annual Plan"
      : "Monthly Plan"
    : "-";
  const pricePerVehicle = parseFloat(limitInfo?.plan?.price_per_vehicle);
  const priceLabel = Number.isNaN(pricePerVehicle)
    ? "-"
    : `${limitInfo?.plan?.currency || ""} ${pricePerVehicle.toFixed(2)}/${
        isAnnual ? "year" : "month"
      }`.trim();

  return (
    <div className="vehicle-table">
      <div className="badge-remaining">Remaining: {vehicleLimit}</div>
      <GenericTable
        title="Vehicles"
        dataSource={dataSource}
        columns={columns}
        enableRowSelection={false}
        headerButtonText={canEdit ? "Add Vehicle" : undefined}
        headerButtonIcon={<PlusOutlined />}
        onHeaderButtonClick={handleAdd}
      />
      <GenericModal
        open={isDeleteOpen}
        title="Delete Vehicle"
        subtitle={`Are you sure you want to delete ${selectedRow?.vehicle}?`}
        highlightText={selectedRow?.vehicle}
        confirmText="Delete"
        cancelText="Cancel"
        iconColor="#DC2626"
        iconBgColor="#DC26261A"
        confirmBtnColor="#DC2626"
        confirmBtnHoverColor="#F87171"
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        showReason={false}
      />
      <VehicleLimitModal
        open={isLimitModalOpen}
        limitMax={limitInfo?.vehicle_limit_total ?? 0}
        vehicleCount={limitInfo?.vehicles_in_use ?? 0}
        planName={planName}
        priceLabel={priceLabel}
        onCancel={() => setIsLimitModalOpen(false)}
        onPurchase={() => {
          setIsLimitModalOpen(false);
          navigate("/subscription");
        }}
      />
    </div>
  );
};

export default Vehicles;
